import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章不存在</h1>
              <p className="text-foreground-600 mb-6">您访问的文章可能已被删除或不存在</p>
              <button
                onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-all"
              >
                返回首页
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "datePublished": article.date,
    "image": article.image,
    "publisher": {
      "@type": "Organization",
      "name": "kaiyun体育",
      "url": "https://yunmatch.github.io/"
    },
    "url": `https://yunmatch.github.io/article/${article.id}`,
    "inLanguage": "zh-CN"
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://yunmatch.github.io/" },
      { "@type": "ListItem", "position": 2, "name": article.category, "item": `https://yunmatch.github.io/category/${article.categorySlug}` },
      { "@type": "ListItem", "position": 3, "name": article.title }
    ]
  };

  const handleCategoryClick = () => {
    navigate(`/category/${article.categorySlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleClick = (aid: string) => {
    navigate(`/article/${aid}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <title>{article.title} - kaiyun体育</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://yunmatch.github.io/article/${article.id}`} />
      <meta property="og:title" content={`${article.title} - kaiyun体育`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://yunmatch.github.io/article/${article.id}`} />
      <meta property="og:image" content={article.image} />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <Navbar />
      <main>
        <article className="pt-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={handleCategoryClick}
                  className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.category}
                </button>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
                  <i className="ri-user-line text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">体育资讯编辑</p>
                </div>
              </div>
            </header>

            <figure className="mb-8 -mx-4 md:mx-0">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-56 md:h-96 object-cover rounded-none md:rounded-xl"
              />
            </figure>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h3:font-heading prose-h3:text-xl prose-h3:text-foreground-950 prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/60">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <div
                    key={ra.id}
                    onClick={() => handleArticleClick(ra.id)}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer border border-background-200/60 hover:border-background-300 transition-all"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-950 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {ra.title}
                      </h3>
                      <p className="text-foreground-500 text-xs">{ra.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}